import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    getFreeRoundsCampaignData,
    getFreeRoundsCampaignGamesData,
    getFreeRoundsCampaignGamesTotalData,
    getGamesByPriceData,
    getPromotionsErrorData,
    getGamesByPriceAreLoadingData,
    shouldUpdateData,
} from '../../../../redux/selectors/backoffice/propmotions-selectors';
import {
    addGameToCampaign,
    clearCampaignData,
    getFreeRoundsCampaign,
    getFreeRoundsCampaignGames,
    getFreeRoundsGamesByPromotionPrice,
    removeGameFromCampaign,
} from '../../../../redux/actions/backoffice/promotions-actions';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorComponent } from '../../../../Containers/Errors/ErrorComponent';
import { Loader } from '../../../../Components/Loader/Loader';
import { Table, Button, Modal, Input, List, TablePaginationConfig } from 'antd';
import { DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { currencyUtils } from 'utils';
import { FilterValue } from 'antd/es/table/interface';
import { SorterResult } from 'antd/es/table/interface';
import { canManagePromotionCampaign } from '../../promotionUtils';

type SortOrder = 'DESCENDING' | 'ASCENDING';
interface SortConfig {
    sortKey: string;
    sortOrder: SortOrder;
}

const DEFAULT_SORT_CONFIG: SortConfig = { sortKey: 'id', sortOrder: 'DESCENDING' };
const DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE = 20;
const DEFAULT_PAGINATION = { pageNumber: 1, pageSize: DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE };

interface Game {
    gameId: number;
    gameName: string;
}

interface TableGame {
    id: number;
    name: string;
    betPrice: number;
    betSize: number;
    priceMultiplier: number;
    used: boolean;
}

const ManageGamesInFreeRoundsCampaign = () => {
    const params = useParams();
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
    const [sortConfig, setSortConfig] = useState<SortConfig>(DEFAULT_SORT_CONFIG);
    const [tableHeight, setTableHeight] = useState(0);
    const games = useSelector(getFreeRoundsCampaignGamesData);
    const gamesTotal = useSelector(getFreeRoundsCampaignGamesTotalData);
    const campaignData = useSelector(getFreeRoundsCampaignData);
    const shouldUpdate = useSelector(shouldUpdateData);
    const availableGamesData = useSelector(getGamesByPriceData);
    const gamesByPriceAreLoading = useSelector(getGamesByPriceAreLoadingData);
    const navigate = useNavigate();
    const error = useSelector(getPromotionsErrorData);
    const [addGamesModalVisible, setAddGamesModalVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [availableGames, setAvailableGames] = useState<any[]>([]);
    const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
    const [gameToAdd, setGameToAdd] = useState<any>(null);
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
    const [gameToDelete, setGameToDelete] = useState<any>(null);

    const calculateTableHeight = useCallback(() => {
        const height = window.innerHeight - 400;
        setTableHeight(height);
    }, []);

    useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    useEffect(() => {
        if (params.campaignId) {
            dispatch(
                getFreeRoundsCampaign({
                    campaignId: params.campaignId,
                    currency: 'USD',
                }),
            );
        }
    }, [params.campaignId, dispatch]);

    useEffect(() => {
        if (params.campaignId) {
            dispatch(
                getFreeRoundsCampaignGames({
                    campaignId: params.campaignId,
                    paginationData,
                    sortConfig,
                    gameNameSearchValue: searchTerm,
                }),
            );
        }
    }, [params.campaignId, dispatch, paginationData, sortConfig, searchTerm]);

    useEffect(() => {
        if (shouldUpdate && params.campaignId) {
            dispatch(
                getFreeRoundsCampaignGames({
                    campaignId: params.campaignId,
                    paginationData,
                    sortConfig,
                    gameNameSearchValue: searchTerm,
                }),
            );
        }
    }, [shouldUpdate, params.campaignId, dispatch, paginationData, sortConfig, searchTerm]);

    useEffect(() => {
        if (campaignData?.bet && campaignData?.currencyCode) {
            dispatch(
                getFreeRoundsGamesByPromotionPrice({
                    price: campaignData.bet,
                    currencyId: currencyUtils.getCurrencyDataByCode(campaignData.currencyCode).id,
                })
            );
        }
    }, [campaignData, dispatch]);

    useEffect(() => {
        if (availableGamesData && campaignData?.bet) {
            const matchedGames = availableGamesData[Number(campaignData.bet)];
            if (matchedGames) {
                setAvailableGames(matchedGames);
            }
        }
    }, [availableGamesData, campaignData?.bet]);

    const isUserCanManageCampaigns = canManagePromotionCampaign(campaignData);

    const handlePagination = (page: number, pageSize?: number) => {
        setPaginationData({ pageNumber: page, pageSize: pageSize || DEFAULT_PAGINATION.pageSize });
    };

    const handleSort = (sorter: SorterResult<any> | SorterResult<any>[]) => {
        if (Array.isArray(sorter) && sorter.length && sorter[0].order) {
            setSortConfig({
                sortKey: sorter[0].field?.toString() || 'id',
                sortOrder: sorter[0].order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
            });
        } else if (!Array.isArray(sorter) && sorter.field && sorter.order) {
            setSortConfig({
                sortKey: sorter.field.toString(),
                sortOrder: sorter.order === 'ascend' ? 'ASCENDING' : 'DESCENDING',
            });
        }
    };

    const onTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<any> | SorterResult<any>[]
    ) => {
        handlePagination(pagination.current ?? 1, pagination.pageSize);
        handleSort(sorter);
    };

    useEffect(() => {
        if (campaignData && !canManagePromotionCampaign(campaignData)) {
            navigate(`/casino/free-rounds-promotion/${params.campaignId}`);
            return;
        }

        if (campaignData && campaignData.status !== 'ONGOING') {
            navigate(`/casino/free-rounds-promotions/${params.campaignId}`);
        }
    }, [campaignData, navigate, params.campaignId]);

    useEffect(() => {
        return () => {
            dispatch(clearCampaignData());
        };
    }, [dispatch]);

    const handleAddGame = (game: any) => {
        setGameToAdd(game);
        setConfirmationModalVisible(true);
    };

    const confirmAddGame = () => {
        dispatch(addGameToCampaign({ campaignId: params.campaignId, gameId: gameToAdd.gameId }));
        setConfirmationModalVisible(false);
        setGameToAdd(null);
    };

    const handleDeleteGame = (game: TableGame) => {
        setGameToDelete(game);
        setDeleteConfirmationVisible(true);
    };

    const confirmDeleteGame = () => {
        if (gameToDelete && params.campaignId) {
            dispatch(removeGameFromCampaign({ campaignId: params.campaignId, gameId: gameToDelete.id }));
            setDeleteConfirmationVisible(false);
            setGameToDelete(null);
        }
    };

    const filteredGames = availableGames.filter(
        (game: any) =>
            game.gameName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            game.gameId.toString().includes(searchQuery),
    );

    const isGameAlreadyAdded = (gameId: number) => {
        return games.some((game: any) => game.id === gameId);
    };

    const columns = [
        {
            title: 'Game ID',
            dataIndex: 'id',
            key: 'id',
            width: 120,
            sorter: true,
        },
        {
            title: 'Game Name',
            dataIndex: 'name',
            key: 'name',
            ellipsis: true,
            sorter: true,
        },
        {
            title: 'Bet Price',
            dataIndex: 'betPrice',
            key: 'betPrice',
            sorter: true,
        },
        {
            title: 'Bet Size',
            dataIndex: 'betSize',
            key: 'betSize',
        },
        {
            title: 'Price Multiplier',
            dataIndex: 'priceMultiplier',
            key: 'priceMultiplier',
        },
        {
            title: 'Used',
            dataIndex: 'used',
            key: 'used',
            render: (used: boolean) => used ? 'Yes' : 'No',
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            render: (_: unknown, record: TableGame) => (
                !record.used && (
                    <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteGame(record)}
                    />
                )
            ),
        }
    ];

    const totalWidth: number = columns.reduce(
        (total: number, col: any) => total + (col.width || 0),
        0,
    );

    return (
        <div className="min-h-0 h-[calc(100vh-64px)] overflow-hidden bg-[#fafbfc] flex flex-col px-6 pb-4">
            <div className="flex justify-between items-center pt-4 pb-4">
                <div className="flex items-center gap-2">
                    <Button icon={<ArrowLeftOutlined />} type="text" onClick={() => navigate(-1)} className="p-6 font-medium" />
                    <div className="">
                        <div className="text-xs text-gray-400 mb-1">
                            <Link to="/casino/free-rounds-promotions" className="hover:underline text-gray-400">Promotions</Link>
                        </div>
                        <h1 className="text-2xl font-semibold text-gray-900">{campaignData?.name || ''}</h1>
                    </div>
                    
                </div>
            </div>
            <div className="flex flex-col bg-white rounded-lg border border-gray-200 px-6 py-4 h-full">
                {error ? (
                    <ErrorComponent error={error} />
                ) : (
                    <div className="flex flex-col">
                        <div className="flex w-full justify-between mb-4">
                            <Input.Search
                                placeholder="Search games"
                                value={searchTerm}
                                onSearch={setSearchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                className="w-96"
                            />
                        </div>

                        {isUserCanManageCampaigns && (
                            <Button
                                type="dashed"
                                className="w-36 mb-4"
                                icon={<PlusOutlined />}
                                onClick={() => setAddGamesModalVisible(true)}
                            >
                                Add Games
                            </Button>
                        )}

                        <Table
                            size="small"
                            columns={columns}
                            dataSource={games}
                            rowKey="id"
                            pagination={{
                                current: paginationData.pageNumber,
                                pageSize: paginationData.pageSize,
                                total: gamesTotal,
                                showSizeChanger: true,
                                onChange: handlePagination,
                            }}
                            onChange={onTableChange}
                            scroll={{ y: tableHeight, x: totalWidth }}
                            className="border-0"
                            bordered={false}
                            rowClassName="border-b border-gray-100 last:border-b-0"
                        />

                        <Modal
                            title="Add Games"
                            open={addGamesModalVisible}
                            onCancel={() => {
                                setSearchQuery('');
                                setAddGamesModalVisible(false);
                            }}
                            footer={null}
                        >
                            <Input
                                placeholder="Search by Game Name or ID"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="mb-4"
                            />
                            {gamesByPriceAreLoading ? (
                                <div className="flex justify-center items-center h-40">
                                    <Loader />
                                </div>
                            ) : (
                                <List
                                    dataSource={filteredGames}
                                    renderItem={(game: Game) => {
                                        const isAdded = isGameAlreadyAdded(game.gameId);
                                        return (
                                            <List.Item
                                                actions={[
                                                    isUserCanManageCampaigns && (
                                                        <Button
                                                            key={game.gameId}
                                                            type={isAdded ? "default" : "primary"}
                                                            size="small"
                                                            onClick={() => handleAddGame(game)}
                                                            disabled={isAdded}
                                                        >
                                                            {isAdded ? "Added" : "Add"}
                                                        </Button>
                                                    )
                                                ]}
                                            >
                                                <div>
                                                    <div className="font-medium">{game.gameName}</div>
                                                    <div className="text-sm text-gray-500">ID: {game.gameId}</div>
                                                </div>
                                            </List.Item>
                                        );
                                    }}
                                    style={{ maxHeight: '400px', overflowY: 'scroll' }}
                                />
                            )}
                        </Modal>

                        <Modal
                            title="Confirm Add Game"
                            open={confirmationModalVisible}
                            onOk={confirmAddGame}
                            onCancel={() => {
                                setConfirmationModalVisible(false);
                                setGameToAdd(null);
                            }}
                            okText="Add"
                            cancelText="Cancel"
                        >
                            <p>Are you sure you want to add the following game to the campaign?</p>
                            {gameToAdd && (
                                <div className="mt-4">
                                    <p><strong>Game Name:</strong> {gameToAdd.gameName}</p>
                                    <p><strong>Game ID:</strong> {gameToAdd.gameId}</p>
                                </div>
                            )}
                        </Modal>

                        <Modal
                            title="Confirm Delete Game"
                            open={deleteConfirmationVisible}
                            onOk={confirmDeleteGame}
                            onCancel={() => {
                                setDeleteConfirmationVisible(false);
                                setGameToDelete(null);
                            }}
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{ danger: true }}
                        >
                            <p>Are you sure you want to remove this game from the campaign?</p>
                            {gameToDelete && (
                                <div className="mt-4">
                                    <p><strong>Game Name:</strong> {gameToDelete.name}</p>
                                    <p><strong>Game ID:</strong> {gameToDelete.id}</p>
                                </div>
                            )}
                        </Modal>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageGamesInFreeRoundsCampaign;
